import React, { useContext } from 'react';
import {
    Button as MuiButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Alert as MuiAlert,
    CircularProgress,
    Box,
    Typography,
    MenuItem,
    Grid,
    Stack,
    Divider,
    useTheme,
    InputAdornment,
    Avatar as MuiAvatar,
    Tooltip,
    Fade
} from '@mui/material';
import { 
    Close as CloseIcon, 
    Warning as WarningIcon, 
    CheckCircle, 
    Info, 
    Error as ErrorIcon 
} from '@mui/icons-material';
import { Eye, EyeOff, Filter, ArrowUpDown } from 'lucide-react';
import { ThemeContext } from '../context/ThemeContext.jsx';
import { DynamicIcon } from './icons.jsx';
import { motion } from 'framer-motion';

// --- Loading Spinner ---
export const LoadingSpinner = ({ size, className = '' }) => {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" p={2} className={className}>
            <CircularProgress size={size || 40} />
        </Box>
    );
};

// --- Avatar (Initials fallback) ---
export const Avatar = ({ src, name, size = 40, className = '', profileDetails }) => {
    // Show 1st & 2nd letter of the name
    const getInitials = (str) => {
        if (!str) return '??';
        const cleanStr = str.replace(/\s/g, '');
        return cleanStr.substring(0, 2).toUpperCase();
    };

    const initials = getInitials(name);

    const avatarComponent = (
        <MuiAvatar
            src={src}
            alt={name}
            className={className}
            imgProps={{ loading: 'lazy' }}
            sx={{
                width: size,
                height: size,
                fontSize: size * 0.4,
                bgcolor: 'var(--primary-color)', // Dynamically use the tenant's primary branding color
                color: '#ffffff',
                fontWeight: 'bold',
                border: '1.5px solid white'
            }}
        >
            {!src ? initials : null}
        </MuiAvatar>
    );

    if (!profileDetails) {
        return avatarComponent;
    }

    const { email, role, domain } = profileDetails;
    
    const cardContent = (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 200 }}>
            <MuiAvatar src={src} alt={name || profileDetails.name} sx={{ width: 64, height: 64, mb: 1, bgcolor: 'var(--primary-color)', fontSize: 24, border: '2px solid white' }}>
                {!src ? initials : null}
            </MuiAvatar>
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                {name || profileDetails.name}
            </Typography>
            {email && (
                <Typography variant="body2" color="text.secondary">
                    {email}
                </Typography>
            )}
            {domain && (
                <Typography variant="body2" color="text.secondary">
                    {domain}
                </Typography>
            )}
            {role && (
                <Typography variant="caption" sx={{ mt: 1, px: 1, py: 0.5, bgcolor: 'action.selected', borderRadius: 1, fontWeight: 'medium' }}>
                    {role}
                </Typography>
            )}
        </Box>
    );

    return (
        <Tooltip
            title={cardContent}
            enterDelay={1000}
            enterNextDelay={1000}
            leaveDelay={200}
            placement="right"
            disableFocusListener
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: 4,
                        p: 0,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }
                }
            }}
        >
            <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }}>
                {avatarComponent}
            </Box>
        </Tooltip>
    );
};

// --- Page Header ---
export const PageHeader = ({ title, description, children, sx = {}, className = '' }) => (
    <Box mb={3} display="flex" flexDirection="column" gap={1} sx={sx} className={className}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                {title}
            </Typography>
            <Box>{children}</Box>
        </Box>
        {description && (
            <Typography variant="body2" color="text.secondary">
                {description}
            </Typography>
        )}
    </Box>
);

// --- Button ---
// Mapping variants: primary -> contained, secondary -> outlined/text, danger -> contained color error
export const Button = ({ children, onClick, variant = 'primary', className = '', type = 'button', disabled = false, size = 'medium', withShadow = false, ...props }) => {
    let muiVariant = 'contained';
    let color = 'primary';

    if (variant === 'secondary') {
        muiVariant = 'outlined';
        color = 'inherit'; // or primary with outlined
    } else if (variant === 'ghost') {
        muiVariant = 'text';
        color = 'inherit';
    } else if (variant === 'danger') {
        muiVariant = 'contained';
        color = 'error';
    }

    return (
        <MuiButton
            type={type}
            onClick={onClick}
            variant={muiVariant}
            color={color}
            disabled={disabled}
            size={size === 'icon' ? 'small' : size}
            className={className}
            sx={{
                textTransform: 'none',
                minWidth: size === 'icon' ? 'auto' : undefined,
                ...(variant === 'secondary' ? {
                    color: 'text.primary',
                    borderColor: 'divider',
                    '&:hover': {
                        borderColor: 'text.primary',
                        bgcolor: 'action.hover'
                    }
                } : {}),
                ...(withShadow ? {
                    boxShadow: 2,
                    bgcolor: muiVariant === 'outlined' ? 'background.paper' : undefined,
                    '&:hover': {
                        boxShadow: 3,
                        bgcolor: muiVariant === 'outlined' ? 'background.paper' : undefined
                    }
                } : {})
            }}
            {...props}
        >
            {children}
        </MuiButton>
    );
};

// --- Modal ---
export const Modal = ({ isOpen, onClose, title, children, zIndex }) => {
    return (
        <Dialog
            open={!!isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ zIndex: zIndex || 1300 }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {title}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {children}
            </DialogContent>
        </Dialog>
    );
};

// --- Confirm Modal ---
export const ConfirmModal = ({
    isOpen,
    onClose = null,
    onConfirm,
    title,
    children = null,
    zIndex = 1300,
    message = null,
    onCancel = null,
    isDestructive = true,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
}) => {
    const handleCancel = onCancel || onClose || (() => {});
    return (
        <Dialog
            open={!!isOpen}
            onClose={handleCancel}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            sx={{ zIndex: zIndex || 1300 }}
        >
            <DialogTitle id="confirm-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color={isDestructive ? 'error' : 'warning'} />
                {title}
            </DialogTitle>
            <DialogContent>
                <Typography id="confirm-dialog-description" component="div">
                    {children || message}
                </Typography>
            </DialogContent>
            <DialogActions>
                <MuiButton onClick={handleCancel} color="inherit">{cancelText}</MuiButton>
                <MuiButton onClick={onConfirm} color={isDestructive ? 'error' : 'primary'} variant="contained" autoFocus>
                    {confirmText}
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};

// --- Alert ---
export const Alert = ({ message, type = 'info' }) => {
    return (
        <MuiAlert severity={type} sx={{ mb: 2 }}>
            {message}
        </MuiAlert>
    );
};


// --- Form Input ---
export const FormInput = ({ label, id, readOnly, InputProps, ...props }) => (
    <Box mb={2}>
        <TextField
            fullWidth
            label={label}
            id={id}
            variant="outlined"
            size="small"
            disabled={readOnly}
            InputProps={{
                readOnly: readOnly,
                ...InputProps
            }}
            {...props}
        />
    </Box>
);

// --- Password Input (Reusable Eye Icon) ---
export const PasswordInput = React.forwardRef(({ label, id, onFocus, onBlur, name, autoComplete, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    // [PasswordInput Debug] 
    // useEffect(() => { console.debug(`[PasswordInput Debug] ${label} - Visible: ${showPassword}`); }, [showPassword]);

    return (
        <Box mb={2}>
            <TextField
                fullWidth
                label={label}
                id={id}
                inputRef={ref}
                type={showPassword ? "text" : "password"}
                name={showPassword ? `${name || 'password'}-visible` : name}
                autoComplete={showPassword ? "new-password" : (autoComplete || "current-password")}
                variant="outlined"
                size="small"
                onFocus={(e) => {
                    setIsFocused(true);
                    if (onFocus) onFocus(e);
                }}
                onBlur={(e) => {
                    setIsFocused(false);
                    if (onBlur) onBlur(e);
                }}
                InputProps={{
                    ...props.InputProps,
                    endAdornment: (
                        <InputAdornment 
                            position="end" 
                        >
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.preventDefault()} // Prevents blur when clicking icon
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                {...props}
            />
        </Box>
    );
});

// --- Form Select ---
// Children are expected to be <option> tags, but MUI Select uses MenuItem.
// If the children are literal options, we might need adjustments.
// Assuming callers pass <option> elements, we might need to intercept children or assume usages will be updated?
// Actually, `FilterPanel.jsx` passes `<option>`. MUI TextField with `select` prop renders a Select.
// However, it expects `MenuItem` as children, NOT `option`.
// We need to support `option` children for backward compatibility OR wrap them?
// Actually, native select is safer for quick migration if we want to avoid refactoring all callsites to generic MenuItems.
// So let's use `Select` native mode or just `TextField` with `SelectProps={{ native: true }}`.
export const FormSelect = ({ label, id, children, value, onChange, ...props }) => (
    <Box mb={2}>
        <TextField
            select
            fullWidth
            label={label}
            id={id}
            value={value}
            onChange={onChange}
            variant="outlined"
            size="small"
            SelectProps={{
                native: true,
            }}
            InputLabelProps={{ shrink: true }}
            {...props}
        >
            {children}
        </TextField>
    </Box>
);

// --- Form Textarea ---
export const FormTextarea = ({ label, id, ...props }) => (
    <Box mb={2}>
        <TextField
            fullWidth
            label={label}
            id={id}
            multiline
            rows={4}
            variant="outlined"
            {...props} // includes value, onChange etc
        />
    </Box>
);

// --- Stat Card ---
export const StatCard = ({ title, value, iconName, colorClass }) => {
    // Extract color from colorClass (e.g., 'bg-blue-500')?
    // This might be tricky if it's a complex tailwind class.
    // For now, render the icon in a Box with the class.
    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                height: '100%'
            }}
        >
            <Box
                className={colorClass}
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white' // Assuming colorClass provides background
                }}
            >
                <DynamicIcon name={iconName} className="w-6 h-6" />
            </Box>
            <Box>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    {title}
                </Typography>
                <Typography variant="h5" color="text.primary" fontWeight="bold">
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

// --- Standard Page Layout (Pro SaaS) ---
export const StandardPageLayout = ({ title, description, headerActions, filterControls, children }) => {
    const theme = useTheme();
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            p: { xs: 1.5, md: 2.5 }
        }}>
            {/* Fixed Header */}
            {(title || description || filterControls || headerActions) && (
                <Box sx={{
                    flexShrink: 0,
                    mb: 2,
                    pb: 1,
                    px: 0,
                    pt: 0
                }}>
                    <Grid container alignItems="flex-start" spacing={2}>
                        <Grid item xs={12} md={4}>
                            {title && (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                >
                                    <Typography variant="h4" fontWeight="700" color="text.primary">
                                        {title}
                                    </Typography>
                                </motion.div>
                            )}
                            {description && (
                                <motion.div
                                    key={description}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.1 }}
                                >
                                    <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                                        {description}
                                    </Typography>
                                </motion.div>
                            )}
                        </Grid>
                        <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0.5 }, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
                            {filterControls}
                            {headerActions}
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Scrollable Content */}
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflowY: 'auto',
                px: 0,
                pt: 1,
                pb: 2,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)', borderRadius: '4px' },
                '&:hover::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' },
            }}>
                {children}
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────
// Filter Dropdown — icon + checkbox popup
// ─────────────────────────────────────────
export function FilterDropdown({ value, onChange, options = [], defaultLabel = 'All Status' }) {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const activeLabel = options.find(o => o.val === value)?.label || defaultLabel;
    const hasFilter = options.length > 0 && options[0].val !== value; // Assuming first option is 'All' or default

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all shadow-sm ${
                    hasFilter
                        ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                        : 'bg-white border-gray-200 text-gray-600 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400'
                } hover:shadow-md z-10`}
            >
                <Filter className="w-3.5 h-3.5" />
                <span>{activeLabel}</span>
                {hasFilter && (
                    <span className="ml-1 flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold">1</span>
                )}
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-1.5 z-50 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-100 dark:border-slate-800 py-2 min-w-[160px]">
                    <p className="px-3 py-1 text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">FILTER BY STATUS</p>
                    {options.map(opt => (
                        <button
                            key={opt.val}
                            onClick={() => { onChange(opt.val); setOpen(false); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                        >
                            <span className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-colors ${
                                value === opt.val
                                    ? 'bg-blue-600 border-blue-600'
                                    : 'border-gray-300 dark:border-slate-600'
                            }`}>
                                {value === opt.val && (
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                                        <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </span>
                            <span className={(opt.color || 'text-gray-700 dark:text-slate-300') + ' font-medium'}>{opt.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────
// Sort Dropdown
// ─────────────────────────────────────────
const defaultSortOptions = [
    { label: 'Last Updated', val: 'updated_desc' },
    { label: 'Oldest Updated', val: 'updated_asc' },
    { label: 'Name (A-Z)', val: 'name_asc' },
    { label: 'Name (Z-A)', val: 'name_desc' },
];

export function SortDropdown({ value, onChange, options = defaultSortOptions, defaultLabel = 'Last Updated' }) {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const activeLabel = options.find(o => o.val === value)?.label || defaultLabel;
    
    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all shadow-sm bg-white border-gray-200 text-gray-600 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400 hover:shadow-md z-10"
            >
                <ArrowUpDown className="w-3.5 h-3.5" />
                <span>{activeLabel}</span>
            </button>
            {open && (
                <div className="absolute right-0 lg:left-0 top-full mt-1.5 z-50 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-100 dark:border-slate-800 py-2 min-w-[160px]">
                    <p className="px-3 py-1 text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">SORT BY</p>
                    {options.map(opt => (
                        <button
                            key={opt.val}
                            onClick={() => { onChange(opt.val); setOpen(false); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                        >
                            <span className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-colors ${
                                value === opt.val
                                    ? 'bg-blue-600 border-blue-600'
                                    : 'border-gray-300 dark:border-slate-600'
                            }`}>
                                {value === opt.val && (
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                                        <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </span>
                            <span className="text-gray-700 dark:text-slate-300 font-medium">{opt.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}